import { brand, dark, light, fontFamily } from './tokens';

// Paleta de los gráficos (Chart.js) de PrestaPlus+, derivada de los tokens de la dirección A "Marino".
// Fuente de verdad: DESIGN.md (sección "Gráficos"). Los colores salen de los tokens; solo las
// variantes que hacen falta cuando se necesitan más de cinco series (morado, cian y magenta) se
// definen aquí, una por modo.
//
// Contraste de cada color contra la superficie de la tarjeta (medido el 2026-10-01 con contrast.js):
//   claro (sobre #FFFFFF): entre 4,52:1 y 9,39:1; oscuro (sobre #16222F): entre 4,79:1 y 9,22:1.
// Todos pasan 3:1, que es lo que piden los elementos gráficos (WCAG 1.4.11). Las variantes de la
// segunda y tercera vuelta también (graficos.test.js lo comprueba para las 24 primeras series).

const variantes = {
	light: { morado: '#6A4C93', cian: '#00838F', magenta: '#AD1457' },
	dark: { morado: '#C3A6F0', cian: '#6DD3DE', magenta: '#E889C7' }
};

// Orden pensado para que dos porciones vecinas de un pastel no se parezcan: marino, ámbar, verde,
// celeste, rojo, morado, cian y magenta. En oscuro el celeste es el del logo, porque `info` coincide
// con `primary` (#9CC3EE) y no se distinguiría.
const serieDe = (modo) => {
	const t = modo === 'dark' ? dark : light;
	const v = variantes[modo];
	return [
		t.primary,
		t.warning,
		t.success,
		modo === 'dark' ? brand.celeste : t.info,
		t.error,
		v.morado,
		v.cian,
		v.magenta
	];
};

// Contraste mínimo de un elemento gráfico contra la tarjeta (WCAG 1.4.11).
const CONTRASTE_MINIMO = 3;

const aRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const aHex = (rgb) => '#' + rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('').toUpperCase();
const luminancia = (hex) => {
	const [r, g, b] = aRgb(hex)
		.map((c) => c / 255)
		.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contraste = (a, b) => {
	const [alto, bajo] = [luminancia(a), luminancia(b)].sort((x, y) => y - x);
	return (alto + 0.05) / (bajo + 0.05);
};
// Mezcla un color con otro (blanco o negro) en la proporción `t`.
const mezclar = (hex, destino, t) => aHex(aRgb(hex).map((c, i) => c + (aRgb(destino)[i] - c) * t));

/**
 * Variante sólida del color `base` para la vuelta `vuelta` (1 o 2) de la serie, sin transparencia.
 * Hacia el lado que AUMENTA el contraste con la tarjeta (más oscuro en claro, más claro en oscuro)
 * va un tono marcado; hacia el otro lado, el tono más suave que todavía llega a 3:1.
 */
const variante = (modo, base, vuelta) => {
	const superficie = modo === 'dark' ? dark.surface : light.surface;
	const mas = modo === 'dark' ? '#FFFFFF' : '#000000'; // más contraste
	const menos = modo === 'dark' ? '#000000' : '#FFFFFF'; // menos contraste
	if (vuelta === 1) return mezclar(base, mas, 0.4);
	for (let t = 0.6; t > 0; t -= 0.05) {
		const color = mezclar(base, menos, t);
		if (contraste(color, superficie) >= CONTRASTE_MINIMO) return color;
	}
	return base;
};

/**
 * Color de la serie número `indice`. Pasada la octava, repite los colores en tonos sólidos más
 * marcados (vuelta 2) y más suaves (vuelta 3 en adelante), siempre con 3:1 o más sobre la tarjeta.
 */
const colorSerie = (modo, indice) => {
	const serie = serieDe(modo);
	const vuelta = Math.min(Math.floor(indice / serie.length), 2);
	const base = serie[indice % serie.length];
	return vuelta === 0 ? base : variante(modo, base, vuelta);
};

/**
 * Formato compacto de un monto para las marcas del eje (p. ej. C$600k, C$1.9M). Los tooltips
 * siguen con `formatearMonto`: aquí solo importa ocupar poco ancho.
 */
export const formatearMontoCompacto = (valor, simbolo = 'C$') => {
	const numero = Number(valor);
	if (!Number.isFinite(numero)) return '';
	const signo = numero < 0 ? '-' : '';
	const abs = Math.abs(numero);
	const recortar = (n) => String(Math.round(n * 10) / 10);
	if (abs >= 1e6) return `${signo}${simbolo}${recortar(abs / 1e6)}M`;
	if (abs >= 1e3) return `${signo}${simbolo}${recortar(abs / 1e3)}k`;
	return `${signo}${simbolo}${String(Math.round(abs * 100) / 100)}`;
};

/**
 * Texto que lee un lector de pantalla en lugar del gráfico (el <canvas> no tiene texto): el título
 * y cada etiqueta con su valor. Ej.: "Recuperación Diaria: ANA C$1,200.00; LUIS C$800.00".
 * @param {string} titulo
 * @param {{labels: string[], datasets: {data: number[]}[]}} datos Los mismos datos del gráfico.
 * @param {(valor: number) => string} [formato]
 */
export const descripcionGrafico = (titulo, datos, formato = (v) => String(v)) => {
	const valores = (datos && datos.datasets && datos.datasets[0] && datos.datasets[0].data) || [];
	const partes = ((datos && datos.labels) || []).map((etiqueta, i) => `${etiqueta} ${formato(valores[i])}`);
	return partes.length ? `${titulo}: ${partes.join('; ')}` : titulo;
};

/**
 * Paleta de un gráfico para el theme de MUI en uso (`useTheme()`).
 * @param {import('@mui/material').Theme} theme
 */
export const paletaGraficos = (theme) => {
	const modo = theme.palette.mode === 'dark' ? 'dark' : 'light';
	const t = modo === 'dark' ? dark : light;

	return {
		modo,
		serie: serieDe(modo),
		/** Color de la serie `indice` (con vueltas atenuadas). */
		color: (indice) => colorSerie(modo, indice),
		/** Arreglo de `cantidad` colores, para los pasteles. */
		colores: (cantidad) => Array.from({ length: cantidad }, (_, i) => colorSerie(modo, i)),
		primario: theme.palette.primary.main,
		exito: theme.palette.success.main,
		error: theme.palette.error.main,
		texto: theme.palette.text.primary,
		textoApagado: theme.palette.text.secondary,
		rejilla: theme.palette.divider,
		superficie: theme.palette.background.paper,
		tooltipFondo: t.tooltipBg,
		tooltipTexto: t.tooltipText,
		fuente: fontFamily
	};
};

/**
 * Opciones `plugins` de Chart.js con leyenda y burbuja con los colores del theme.
 * @param {ReturnType<typeof paletaGraficos>} paleta
 * @param {{ leyenda?: boolean, posicionLeyenda?: string, etiqueta?: Function }} [opciones]
 *   `etiqueta`: función `(contexto) => string` para el texto de la burbuja.
 */
export const pluginsGrafico = (paleta, { leyenda = false, posicionLeyenda = 'bottom', etiqueta } = {}) => ({
	legend: {
		display: leyenda,
		position: posicionLeyenda,
		labels: {
			color: paleta.texto,
			font: { family: paleta.fuente, size: 13 },
			boxWidth: 14,
			boxHeight: 14,
			padding: 12
		}
	},
	tooltip: {
		backgroundColor: paleta.tooltipFondo,
		titleColor: paleta.tooltipTexto,
		bodyColor: paleta.tooltipTexto,
		titleFont: { family: paleta.fuente, weight: '700' },
		bodyFont: { family: paleta.fuente },
		padding: 10,
		cornerRadius: 12,
		callbacks: etiqueta ? { label: etiqueta } : {}
	}
});

/**
 * Escalas x e y de un gráfico de barras o líneas: rejilla suave, ejes en el color de texto secundario.
 * @param {ReturnType<typeof paletaGraficos>} paleta
 * @param {(valor: number) => string} formatoY Formato de las marcas del eje de los valores (p. ej. `formatearMonto`).
 * @param {{ horizontal?: boolean }} [opciones] Con `horizontal` (barras con `indexAxis: 'y'`), los
 *   valores van en el eje x y los nombres en el y.
 */
export const escalasGrafico = (paleta, formatoY, { horizontal = false } = {}) => {
	const categorias = {
		grid: { display: false },
		border: { color: paleta.rejilla },
		ticks: { color: horizontal ? paleta.texto : paleta.textoApagado, font: { family: paleta.fuente, size: 12 } }
	};
	const valores = {
		beginAtZero: true,
		grid: { color: paleta.rejilla },
		border: { display: false },
		ticks: {
			color: paleta.textoApagado,
			font: { family: paleta.fuente, size: 12 },
			callback: (valor) => formatoY(valor)
		}
	};
	return horizontal ? { x: valores, y: categorias } : { x: categorias, y: valores };
};
